import React from "react";
import Tooltip from "../ui/Tooltip";
import PhysicsOutlineLockBadge from "../ui/PhysicsOutlineLockBadge";
import { MOBILE_TOOLBAR_MEDIA_QUERY } from "./toolbarConstants";

function useIsMobileToolbarViewport() {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(MOBILE_TOOLBAR_MEDIA_QUERY).matches;
  });

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_TOOLBAR_MEDIA_QUERY);
    const handleChange = () => setMatches(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return matches;
}

function OffSlash() {
  return (
    <svg
      className="absolute inset-0 h-[18px] w-[18px]"
      viewBox="0 0 16 16"
      style={{ pointerEvents: "none" }}
      aria-hidden
    >
      <line
        x1="0"
        y1="14"
        x2="14"
        y2="0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ToolButton({
  active = false,
  disabled = false,
  label,
  onClick,
  children,
  showOffSlash = false,
  showMixedBadge = false,
  showPhysicsOutlineLock = false,
  showTooltip = true,
  className = "",
}) {
  const isMobileToolbarViewport = useIsMobileToolbarViewport();
  const tooltipSide = isMobileToolbarViewport ? "left" : "top";
  const button = (
    <button
      type="button"
      className={`viewport-toolbar-button${active ? " viewport-toolbar-button-active" : ""} ${showPhysicsOutlineLock ? "viewport-toolbar-button--physics-outline-lock" : ""} ${className}`.trim()}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="relative flex h-[18px] w-[18px] items-center justify-center">
        {children}
        {showOffSlash && <OffSlash />}
        {showMixedBadge && (
          <span
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-orange-400 border border-current"
            aria-label="Some items individually hidden"
            style={{ pointerEvents: "none" }}
          />
        )}
      </span>
      {showPhysicsOutlineLock ? <PhysicsOutlineLockBadge /> : null}
    </button>
  );

  if (!showTooltip) return button;
  return (
    <Tooltip content={label} side={tooltipSide}>
      {button}
    </Tooltip>
  );
}

function ToolbarGroup({ label, primary, children, muted = false }) {
  return (
    <div
      className={`viewport-toolbar-group${muted ? " viewport-toolbar-group-muted" : ""}`}
      aria-label={label}
    >
      {primary}
      {children && <div className="viewport-toolbar-group-items">{children}</div>}
    </div>
  );
}

export { ToolButton, ToolbarGroup, useIsMobileToolbarViewport };
